/**
 * Error Boundary Component
 * @author Maheen Nasir
 * @description A React error boundary component that catches JavaScript errors in its child component tree
 * and displays a fallback UI when an error occurs.
 */

import React, { Component } from "react";
import type { ErrorInfo } from "react";
import { ErrorFallback } from "./error-fallback";
import type { Props, State } from "./types";

/**
 * ErrorBoundary component that catches JavaScript errors in its child component tree.
 * Displays a fallback UI when an error occurs.
 * @extends {Component<Props, State>}
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  /**
   * Update state when an error occurs
   * @param {Error} error - The error that was thrown
   * @returns {State} The new state with error information
   */
  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error information for debugging
   * @param {Error} error - The error that was thrown
   * @param {ErrorInfo} errorInfo - Additional error information
   */
  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Uncaught error:", error, errorInfo);
  }

  /**
   * Reset error state and reload the page
   * @private
   */
  private handleReset = (): void => {
    window.location.reload();
  };

  /**
   * Render method for ErrorBoundary component
   * @returns {React.ReactNode} The rendered component
   */
  public render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <ErrorFallback
          error={error}
          resetError={this.handleReset}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
